import React, { useEffect, useMemo, useRef, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import type { JSX } from 'react';
import { ErrorMessage } from 'shouldve-been-mui';
import type Vessel from '../../Models/VesselModel';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';

type AssignSortNumberDialogProps = {
    controller: boolean,
    currentSortNo: number | null,
    existingVessels: Vessel[],
    onClose: (newValue: number, shouldUpdate: boolean) => void,
};

function AssignSortNumberDialog(props: Readonly<AssignSortNumberDialogProps>): JSX.Element {

    const formRef: React.RefObject<HTMLFormElement> = useRef<HTMLFormElement>(null);

    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [sortNo, setSortNo] = useState<number | null>(null);
    const [windowSize, setWindowSize] = useState<[number, number]>([1920, 1080]);

    const filteredExistingVessels: Vessel[] = useMemo<Vessel[]>(() => props.existingVessels.filter((existingVessel: Vessel) => existingVessel.sortNo !== null), [props.existingVessels]);

    const sortedExistingVessels: Vessel[] = useMemo<Vessel[]>(() => [...filteredExistingVessels].sort((a: Vessel, b: Vessel) => (a.sortNo ?? 0) - (b.sortNo ?? 0)), [filteredExistingVessels]);

    const tableRows: JSX.Element[] = useMemo<JSX.Element[]>(() => {

        const lastSortNo: number = sortedExistingVessels[sortedExistingVessels.length - 1]?.sortNo ?? 0;

        const newTableRows: JSX.Element[] = [];
        
        sortedExistingVessels.forEach((existingVessel: Vessel) => {

            newTableRows.push(
                <TableRow
                    key={'button_' + existingVessel.vesselId.toString()}
                >
                    <TableCell 
                        colSpan={2}
                        style={{
                            padding: 0,
                            textAlign: 'center',
                            border: 'none',
                        }}
                    >
                        <Button
                            onClick={(): void => setSortNo(existingVessel.sortNo ?? 0)}
                            color={sortNo === existingVessel.sortNo ? 'secondary' : 'primary'}
                            variant='contained'
                            style={{
                                minWidth: '100px',
                                maxWidth: '100px',
                                minHeight: '20px',
                                maxHeight: '20px',
                                lineHeight: 'normal',
                                fontSize: '10px',
                            }}
                        >
                            {sortNo === existingVessel.sortNo ? 'Selected' : 'Insert Here'}
                        </Button>
                    </TableCell>
                </TableRow>,
            );
            
            newTableRows.push(
                <TableRow
                    key={existingVessel.vesselId}
                >
                    <TableCell style={{
                        border: 'none',
                    }}>
                        {existingVessel.vesselName}
                    </TableCell>
                    <TableCell style={{
                        border: 'none',
                    }}>
                        {existingVessel.sortNo}
                    </TableCell>
                </TableRow>,
            );
        });

        newTableRows.push(
            <TableRow
                key='last_button'
            >
                <TableCell 
                    colSpan={2}
                    style={{
                        padding: 0,
                        textAlign: 'center',
                        border: 'none',
                    }}
                >
                    <Button
                        onClick={(): void => setSortNo(lastSortNo + 1)}
                        color={sortNo === lastSortNo + 1 ? 'secondary' : 'primary'}
                        variant='contained'
                        style={{
                            minWidth: '100px',
                            maxWidth: '100px',
                            minHeight: '20px',
                            maxHeight: '20px',
                            lineHeight: 'normal',
                            fontSize: '10px',
                        }}
                    >
                        {sortNo === lastSortNo + 1 ? 'Selected' : 'Insert Here'}
                    </Button>
                </TableCell>
            </TableRow>,
        );

        return newTableRows;

    }, [sortNo, sortedExistingVessels]);

    useEffect(() => {

        function updateSize(): void {
            setWindowSize([window.innerWidth, window.innerHeight]);
        }

        window.addEventListener('resize', updateSize);
        updateSize();
        return () => window.removeEventListener('resize', updateSize);
    }, []);

    function handleClose(shouldUpdate: boolean): void {

        if (sortNo === null && shouldUpdate) {
            setErrorMessage('No position selected');
            return;
        }

        setSortNo(null);

        props.onClose(sortNo ?? 0, shouldUpdate); //the 0 default will never be used, just needed for typecheck
    }

    return (
        <Dialog 
            open={props.controller} 
            onClose={(): void => handleClose(false)}
            TransitionProps={{
                onEntered: (): void => setSortNo(props.currentSortNo),
            }}
        >
            <DialogTitle>Assign Sorting</DialogTitle>
            <DialogContent>
                <div>
                    <div style={{
                        maxHeight: windowSize[1] / 2,
                        overflow: 'auto',
                    }}>
                        <form ref={formRef}>
                            <TableContainer>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell style={{
                                                border: 'none',
                                            }}>
                                                Vessel Name
                                            </TableCell>
                                            <TableCell style={{
                                                border: 'none',
                                            }}>
                                                Sort Number
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {tableRows}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </form>
                    </div>

                    <ErrorMessage
                        message={errorMessage}
                        maxWidth='500px'
                    />
                </div>
            </DialogContent>
            <DialogActions>
                <Button
                    onClick={(): void => handleClose(false)}
                    color='error'
                    variant='contained'
                    style={{
                        width: '90px',
                        height: '36px',
                        lineHeight: 'normal',
                        marginRight: '10px',
                    }}
                >
                    Close
                </Button>
                <Button
                    onClick={(): void => handleClose(true)}
                    color='primary'
                    variant='contained'
                    style={{
                        width: '90px',
                        height: '36px',
                        lineHeight: 'normal',
                        marginRight: '10px',
                    }}
                >
                    Assign
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default AssignSortNumberDialog;